import React from 'react';
import { connect } from 'react-redux';
import { setLocale } from '../../actions/intl';
import COPY from './copy.json';
import SERVICES from './services.json';
import {
  t, PHONE, PHONE_LABEL, WA, initReveal, Figure,
  IconPhone, IconTick, IconArrow,
  Header, Footer, CallBar
} from './SiteChrome';

/*
 * Halaman layanan: Car recovery — port dari site/service-recovery.html.
 *
 * Empat halaman layanan lain sekarang ada di ServicePage.js, digerakkan
 * services.json. Halaman ini tetap berdiri sendiri: punya tiga bagian yang tidak
 * dimiliki halaman lain (kapan perlu recovery, sambil menunggu, tabel harga 8
 * baris) dan strukturnya sudah diverifikasi cocok dengan HTML paket client.
 *
 * BELUM BERES: angka AED 300 di kartu hero dan di LD_SERVICE berasal dari
 * prototipe client. DB bilang recovery dihitung per jarak (basePrice 4-8 dikali
 * multiplierValue 15 per lokasi), jadi tidak ada satu harga tetap. Perlu rumus
 * resmi dari client sebelum angka ini bisa dibenarkan — semua harga lain di
 * situs sudah bersumber dari tabel Pricing.
 */

const WHEN = ['nostart', 'accident', 'overheat', 'gearbox', 'battery', 'sand'];
const PRICING = ['city', 'perkm', 'interemirate', 'carpark', 'desert', 'bike', 'night', 'vat'];
const WAIT = [1, 2, 3, 4, 5];
const FAQ_KEYS = [1, 2, 3, 4, 5];

// Kartu layanan lain. Mengikuti katalog asli di DB (lihat services.json) —
// harga = subkategori termurah di kategori itu, bukan angka karangan.
const OTHER = ['tyre', 'fuel', 'carservices', 'carwash'].map(key => ({
  key,
  href: SERVICES[key].path,
  price: `AED ${Math.min(...SERVICES[key].items.map(i => i.price).filter(p => p != null))}`
}));

// Schema sengaja selalu en-US: satu URL = satu bahasa untuk crawler.
const LD_SERVICE = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Vehicle recovery and towing',
  serviceType: 'Car recovery',
  provider: { '@type': 'AutoRepair', name: 'Bas Recovery', telephone: PHONE, url: 'https://basrecovery.com/' },
  areaServed: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain']
    .map(name => ({ '@type': 'City', name })),
  offers: { '@type': 'Offer', price: '300', priceCurrency: 'AED',
    description: 'Starting price within a city zone, first 10 km included' }
};

const LD_FAQ = {
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: FAQ_KEYS.map(n => ({
    '@type': 'Question', name: COPY['en-US']['srec.faq.' + n + '.q'],
    acceptedAnswer: { '@type': 'Answer', text: COPY['en-US']['srec.faq.' + n + '.a'] }
  }))
};

const LD_CRUMB = {
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://basrecovery.com/' },
    { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://basrecovery.com/service-recovery' },
    { '@type': 'ListItem', position: 3, name: 'Car recovery' }
  ]
};

class ServiceRecovery extends React.Component {
  state = { navOpen: false };

  toggleNav = () => this.setState(s => ({ navOpen: !s.navOpen }));
  closeNav = () => this.setState({ navOpen: false });

  switchLang = () => {
    const next = this.props.currentLocale === 'ar' ? 'en-US' : 'ar';
    document.documentElement.lang = next;
    this.props.setLocale({ locale: next });
  };

  componentDidMount() {
    this.onKey = e => { if (e.key === 'Escape') this.closeNav(); };
    document.addEventListener('keydown', this.onKey);
    this.io = initReveal();
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    if (this.io) this.io.disconnect();
  }

  render() {
    const { navOpen } = this.state;
    const L = this.props.currentLocale || 'en-US';
    const tx = (k, v) => t(L, k, v);

    return (
      <div>
        <a className="skip" href="#main">{tx('nav.skip')}</a>

        <Header
          locale={L} current="/service-recovery"
          navOpen={navOpen}
          onToggleNav={this.toggleNav}
          onCloseNav={this.closeNav}
          onSwitchLang={this.switchLang}
        />

        <main id="main">
          {/* ── HERO ──
              Halaman layanan dicapai dari pencarian dengan niat sudah terbentuk,
              jadi dibuka dengan harga, ETA dan tombol telepon, bukan pitch. */}
          <section className="hero" style={{ paddingBlock: 'clamp(40px,6vw,72px) clamp(48px,7vw,80px)' }}>
            <div className="wrap">
              <nav aria-label="Breadcrumb" style={{ marginBottom: 'var(--sp-5)' }}>
                <ol style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '9px', flexWrap: 'wrap',
                  fontSize: 'var(--fs-xs)', color: 'var(--on-night-2)' }}>
                  <li><a href="/" style={{ color: 'var(--on-night-2)' }}>{tx('nav.home')}</a></li>
                  <li aria-hidden="true">/</li>
                  <li><a href="/service-recovery" style={{ color: 'var(--on-night-2)' }}>{tx('nav.services')}</a></li>
                  <li aria-hidden="true">/</li>
                  <li><span style={{ color: 'var(--orange-hot)' }}>{tx('srec.crumb')}</span></li>
                </ol>
              </nav>

              <div className="hero-grid">
                <div>
                  <p className="eyebrow">{tx('srec.eyebrow')}</p>
                  <h1 style={{ fontSize: 'var(--fs-h1)', maxWidth: '22ch' }}>{tx('srec.h1')}</h1>
                  <p className="lead">{tx('srec.lead')}</p>
                  <div className="hero-cta">
                    <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                      <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
                    </a>
                    <a className="btn btn-ghost btn-lg" href="/contact">{tx('cta.callback')}</a>
                  </div>
                </div>

                <aside className="eta-card" aria-label={tx('srec.etaAria')}>
                  <div style={{ display: 'flex', gap: 'var(--sp-6)', flexWrap: 'wrap' }}>
                    <div>
                      <div className="eta-big" style={{ fontSize: 'clamp(40px,5vw,58px)' }}>
                        <span style={{ fontSize: '.45em' }}>AED </span>300
                      </div>
                      <p className="eta-label">{tx('srec.priceLabel')}</p>
                    </div>
                    <div>
                      <div className="eta-big" style={{ fontSize: 'clamp(40px,5vw,58px)' }}>
                        25<span> {tx('eta.unit')}</span>
                      </div>
                      <p className="eta-label">{tx('srec.etaLabel')}</p>
                    </div>
                  </div>
                  <div className="eta-rule" />
                  <div className="eta-rows">
                    {[1, 2, 3, 4].map(n => (
                      <div className="eta-row" key={n}>
                        <IconTick /><span><b>{tx(`srec.spec.${n}b`)}</b>{tx(`srec.spec.${n}`)}</span>
                      </div>
                    ))}
                  </div>
                </aside>
              </div>
            </div>
          </section>

          {/* ── KAPAN PERLU RECOVERY ── */}
          <section>
            <div className="wrap">
              <div className="sec-head">
                <h2>{tx('srec.when.h2')}</h2>
                <p className="lead">{tx('srec.when.lead')}</p>
              </div>
              <div className="svc-grid">
                {WHEN.map(k => (
                  <div className="svc" key={k}>
                    <h3>{tx(`srec.when.${k}.title`)}</h3>
                    <p>{tx(`srec.when.${k}.body`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── HARGA ──
              Yang paling dicari orang dan paling jarang ditemukan. */}
          <section className="band-warm">
            <div className="wrap">
              <div className="sec-head">
                <h2>{tx('srec.price.h2')}</h2>
                <p className="lead">{tx('srec.price.lead')}</p>
              </div>
              <div className="cov-grid">
                {PRICING.map(k => (
                  <div className="cov" key={k}>
                    <span>{tx(`srec.price.${k}`)}<small>{tx(`srec.price.${k}.note`)}</small></span>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 'var(--sp-5)', color: 'var(--ink-2)', fontSize: 'var(--fs-sm)' }}>
                {tx('srec.price.foot')}
              </p>
            </div>
          </section>

          {/* ── SAMBIL MENUNGGU ── */}
          <section>
            <div className="wrap split">
              <div>
                <h2>{tx('srec.wait.h2')}</h2>
                <p className="lead">{tx('srec.wait.lead')}</p>
                <div className="ticks">
                  {WAIT.map(n => (
                    <div className="tick" key={n}>
                      <IconTick /><span><strong>{tx(`srec.wait.${n}b`)}</strong>{tx(`srec.wait.${n}`)}</span>
                    </div>
                  ))}
                </div>
                <div className="emerg" style={{ marginTop: 'var(--sp-6)', maxWidth: '52ch' }}>
                  <b>{tx('srec.emergTitle')}</b>
                  {tx('srec.emergBody')}
                </div>
              </div>
              <Figure photo="recovery" alt={tx('srec.figureAlt')} caption={tx('srec.figure')} />
            </div>
          </section>

          {/* ── LAYANAN LAIN ── */}
          <section className="band-sand">
            <div className="wrap">
              <div className="sec-head"><h2>{tx('srec.other.h2')}</h2></div>
              <div className="svc-grid">
                {OTHER.map(o => (
                  <a className="svc" href={o.href} key={o.href}>
                    <h3>{tx(`svc.card.${o.key}.title`)}</h3>
                    <p>{tx(`svc.card.${o.key}.body`)}</p>
                    <span className="svc-more">{tx('svc.from')} {o.price} <IconArrow /></span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section>
            <div className="wrap-narrow">
              <div className="sec-head"><h2>{tx('srec.faq.h2')}</h2></div>
              <div className="faq">
                {FAQ_KEYS.map((n, i) => (
                  <details key={n} open={i === 0}>
                    <summary>{tx(`srec.faq.${n}.q`)}</summary>
                    <div><p>{tx(`srec.faq.${n}.a`)}</p></div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ── CTA ── */}
          <section className="band-night">
            <div className="wrap" style={{ textAlign: 'center' }}>
              <h2 style={{ maxWidth: '26ch', marginInline: 'auto' }}>{tx('srec.cta.h2')}</h2>
              <p style={{ maxWidth: '50ch', margin: 'var(--sp-4) auto var(--sp-6)' }}>{tx('srec.cta.lead')}</p>
              <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
              </a>
            </div>
          </section>
        </main>

        <Footer locale={L} />
        <CallBar locale={L} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_SERVICE) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_FAQ) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_CRUMB) }} />
      </div>
    );
  }
}

const mapState = state => ({ currentLocale: state?.intl?.locale });
const mapDispatch = { setLocale };

export default connect(mapState, mapDispatch)(ServiceRecovery);
